import { Preferences } from "@capacitor/preferences";
import { IS_NATIVE_APP } from "../config/appTarget.js";

const memoryStore = new Map<string, string>();

async function read(key: string): Promise<string | null> {
  if (IS_NATIVE_APP) {
    const { value } = await Preferences.get({ key });
    return value;
  }
  return memoryStore.get(key) ?? null;
}

async function write(key: string, value: string): Promise<void> {
  if (IS_NATIVE_APP) {
    await Preferences.set({ key, value });
    return;
  }
  memoryStore.set(key, value);
}

async function remove(key: string): Promise<void> {
  if (IS_NATIVE_APP) {
    await Preferences.remove({ key });
    return;
  }
  memoryStore.delete(key);
}

export const AppStorageKeys = {
  ONBOARDING_COMPLETED: "onboarding_completed",
  ONBOARDING_USER_TYPE: "onboarding_user_type",
  ONBOARDING_PAWMATCH_PREFS: "onboarding_pawmatch_prefs",
  PAWMATCH_SESSION: "pawmatch_session",
  PAWMATCH_PREFERENCES: "pawmatch_preferences",
  AUTH_RETURN_TO: "auth_return_to",
  PAYMENT_INTEREST_SURVEY_DATE: "pawseum_payment_interest_survey_date",
  PUSH_PERMISSION_PROMPTED: "push_permission_prompted",
  LANGUAGE: "app_language",
} as const;

export async function getString(key: string): Promise<string | null> {
  return read(key);
}

export async function setString(
  key: string,
  value: string | null,
): Promise<void> {
  if (value === null) {
    await remove(key);
    return;
  }
  await write(key, value);
}

export async function getOnboardingCompleted(): Promise<boolean> {
  return (await read(AppStorageKeys.ONBOARDING_COMPLETED)) === "true";
}

export async function setOnboardingCompleted(
  completed: boolean,
): Promise<void> {
  if (completed) {
    await write(AppStorageKeys.ONBOARDING_COMPLETED, "true");
  } else {
    await remove(AppStorageKeys.ONBOARDING_COMPLETED);
  }
}

export async function getJson<T>(key: string): Promise<T | null> {
  const raw = await read(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export async function setJson(key: string, value: unknown): Promise<void> {
  if (value === null || value === undefined) {
    await remove(key);
    return;
  }
  await write(key, JSON.stringify(value));
}

// ─── Push notification permission ────────────────────────────────────────────

/** Whether we've already shown the OS push-permission prompt to this user. */
export async function getPushPermissionPrompted(): Promise<boolean> {
  return (await read(AppStorageKeys.PUSH_PERMISSION_PROMPTED)) === "true";
}

export async function setPushPermissionPrompted(
  prompted: boolean,
): Promise<void> {
  if (prompted) {
    await write(AppStorageKeys.PUSH_PERMISSION_PROMPTED, "true");
  } else {
    await remove(AppStorageKeys.PUSH_PERMISSION_PROMPTED);
  }
}

// ─── PawMatch session ────────────────────────────────────────────────────────

/** Serialised PawMatch feed slice persisted between app launches (iOS only). */
export interface PawMatchSession {
  currentIndex: number;
  cursor: string | null;
  swipedAnimalIds: string[];
  feedPrefsKey: string | null;
  savedAt: number;
}

const PAWMATCH_TTL_MS = 30 * 60 * 1000;

export const pawmatchStorage = {
  async saveSession(session: PawMatchSession): Promise<void> {
    if (!IS_NATIVE_APP) {
      // Web: use sessionStorage so the data lives only for the browser tab.
      try {
        sessionStorage.setItem(
          AppStorageKeys.PAWMATCH_SESSION,
          JSON.stringify(session),
        );
      } catch {
        // sessionStorage may be unavailable (private browsing quota); silently skip.
      }
      return;
    }
    await setJson(AppStorageKeys.PAWMATCH_SESSION, session);
  },

  async loadSession(): Promise<PawMatchSession | null> {
    let raw: PawMatchSession | null = null;

    if (!IS_NATIVE_APP) {
      try {
        const stored = sessionStorage.getItem(AppStorageKeys.PAWMATCH_SESSION);
        raw = stored ? (JSON.parse(stored) as PawMatchSession) : null;
      } catch {
        raw = null;
      }
    } else {
      raw = await getJson<PawMatchSession>(AppStorageKeys.PAWMATCH_SESSION);
    }

    if (!raw) return null;

    // Invalidate stale sessions (TTL).
    if (Date.now() - raw.savedAt > PAWMATCH_TTL_MS) {
      await pawmatchStorage.clearSession();
      return null;
    }

    return raw;
  },

  async clearSession(): Promise<void> {
    if (!IS_NATIVE_APP) {
      try {
        sessionStorage.removeItem(AppStorageKeys.PAWMATCH_SESSION);
      } catch {
        // ignore
      }
      return;
    }
    await remove(AppStorageKeys.PAWMATCH_SESSION);
  },
};
